import React, { useEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import TextInput from "../../common/textfield/TextInput";
import { Col, Row, Table } from "react-bootstrap";
import { DateSelectorWithoutFormik } from "../../common/textfield/DateSelector";
import { Skeleton, TableCell } from "@mui/material";
import { Button } from "../../common/Button";
import { useFormik } from "formik";
import { failed } from "../../common/Toastify";
import { searchPatient } from "../../apps/api/Patient";
import CustomPagination from "../../common/components/Pagination/CustomPagination";
import moment from "moment";
import { ViewDate } from "../../../utils/DateSupport";

// Titles for patient list table header
const patientListTitle = [
  "Name",
  "Gender",
  "DOB",
  "Patient Identifier",
  "Address",
  "City",
  "State/Province",
];

export default function TaskPatientSelect({
  orgId,
  show,
  onHide,
  onPatientSelected,
  initialPatient,
}) {
  // state
  const [isLoading, setIsLoading] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [filterData, setFilterData] = useState([]);
  const [isRegion, setIsRegion] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(initialPatient?.id || null);
  const [selectedPatientObj, setSelectedPatientObj] = useState(initialPatient || null);
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // patient search formik
  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      birthDate: "",
      healthCardNumber: "",
    },
    validate: values => {
      // require at least one field
      if (
        !values.firstName &&
        !values.lastName &&
        !values.birthDate &&
        !values.healthCardNumber
      ) {
        return { allSearchFieldsEmpty: "At least one field must be filled out." };
      }
      return {};
    },
    onSubmit: (values) => {
      setBtnLoading(true);
      setIsLoading(true);

      // Fix date/timezone
      const convertDateFormat = (originalDate) => {
        const dateObject = new Date(originalDate);
        const day = String(dateObject.getDate()).padStart(2, '0');
        const month = String(dateObject.getMonth() + 1).padStart(2, '0');
        const year = dateObject.getFullYear();
        return `${month}-${day}-${year}`;
      };

      const timeZoneGuess = moment.tz.guess();
      const timeZoneParts = timeZoneGuess.split('/');
      let region = timeZoneParts[0];
      let dateData = "";
      if (!values.birthDate) {
        dateData = "";
      } else {
        if ((region === "America" || region === "Canada") && isRegion === true) {
          let dateDataNew = convertDateFormat(values.birthDate);
          let currentDate = new Date(dateDataNew);
          currentDate.setDate(currentDate.getDate() + 1);
          dateData = convertDateFormat(currentDate);
          setIsRegion(false);
        } else {
          dateData = convertDateFormat(values.birthDate);
        }
      }
      values.birthDate = dateData;

      searchPatient({ ...values, org_id: orgId })
        .then((res) => {
          if (res.status === true) {
            setFilterData(res?.result);
          } else {
            setFilterData([]);
            failed(res.error);
          }
        })
        .catch((res) => {
          failed(
            res?.response?.data?.message ||
            res?.response?.data?.error ||
            res.message
          );
        })
        .finally(() => {
          setBtnLoading(false);
          setIsLoading(false);
        });
    },
  });

  // Reset form/patients when modal opens/closes
  useEffect(() => {
    if (show) {
      formik.resetForm();
      setFilterData([]);
      setSelectedPatientId(initialPatient?.id || null);
      setSelectedPatientObj(initialPatient || null);
      setPage(0);
    }
    // eslint-disable-next-line
  }, [show]);

  const handleDateChange = (val) => {
    const isValidDate = moment(val, 'MMM-DD-YYYY').isValid();
    setIsRegion(true);
    if (!val) {
      formik.setFieldValue("birthDate", "");
    } else if (isValidDate) {
      formik.setFieldValue("birthDate", val.format("YYYY-MM-DD"));
    } else {
      formik.setFieldError('birthDate', 'Invalid date format');
    }
  };

  const handlePatientClick = (data) => {
    const baseUrl = window.location.origin;
    const patientId = data?.id;
    const patientName = data?.name?.[0]?.text;
    const url = `${baseUrl}/app/patients-details?patientId=${patientId}`//&patientName=${patientName}`;
    window.open(url, "_blank");
  };

  // Table rows
  const renderRows = () => {
    if (isLoading) {
      return [...Array(8)].map((_, idx) => (
        <tr key={idx}>
          {Array(8).fill().map((__, i) => (
            <td key={i}><Skeleton animation="wave" /></td>
          ))}
        </tr>
      ));
    }
    if (filterData?.length > 0) {
      return filterData
        .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        .map((data, index) => {
          const formattedDateString = data?.birthDate ? ViewDate(data?.birthDate) : "-";
          const hcnIdentifier = data?.identifier?.find(identifier => identifier?.type === "HCN") || {};
          const hcnValue = hcnIdentifier && hcnIdentifier?.value ? hcnIdentifier?.value : '-';
          return (
            <tr key={data?.id || index}>
              <td>
                <input
                  style={{ cursor: "pointer" }}
                  type='radio'
                  name={`radio`}
                  value={data?.id}
                  checked={selectedPatientId === data?.id}
                  onChange={() => {
                    setSelectedPatientId(data?.id);
                    setSelectedPatientObj(data);
                  }}
                />
              </td>
              <td>
                <span
                  style={{ cursor: "pointer" }}
                  onClick={() => handlePatientClick(data)}
                >
                  {data?.name?.[0]?.text?.split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                </span>
              </td>
              <td style={{ textTransform: "capitalize" }}>{data?.gender}</td>
              <td>{formattedDateString}</td>
              <td>{hcnValue}</td>
              <td>{data?.address?.[0]?.line?.[0]}</td>
              <td style={{ textTransform: "capitalize" }}>{data?.address?.[0]?.city}</td>
              <td>{data?.address?.[0]?.state || data?.address?.[0]?.province}</td>
            </tr>
          );
        });
    }
    return (
      <tr>
        <TableCell colSpan="8" style={{ textAlign: "center" }}>
          No Records Found
        </TableCell>
      </tr>
    );
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Select Patient</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="common-form border-fields" onSubmit={formik.handleSubmit}>
          <Row>
            <Col >
              <TextInput
                keyField={"firstName"}
                label={"First Name"}
                formik={formik}
                placeholder={"First Name"}
                hideRequired={true}
                className="field-wrap text area"
              />
            </Col>
            <Col>
              <TextInput
                keyField={"lastName"}
                label={"Last Name"}
                formik={formik}
                placeholder={"Last Name"}
                hideRequired={true}
              />
            </Col>
            <Col>
              <DateSelectorWithoutFormik
                formik={formik}
                keyField={'birthDate'}
                label="DOB"
                value={formik.values.birthDate}
                handleChange={handleDateChange}
                maxDate={moment()}
                hideRequired={true}
              />
            </Col>
            <Col>
              <TextInput
                keyField={"healthCardNumber"}
                label={"Patient Identifier"}
                formik={formik}
                placeholder={"Patient Identifier"}
                hideRequired={true}
              />
            </Col>
          </Row>

          <div style={{display: "flex", justifyContent: "center", marginTop: "20px"}}>
            <Button
                type="submit"
                variant="primary"
                disabled={btnLoading}
                loading={btnLoading}
            >
                {btnLoading ? "Searching..." : "Search"}
            </Button>
          </div>
          {formik?.errors["allSearchFieldsEmpty"] && (
            <div style={{
              fontSize: "14px", color: "#dc3545", paddingTop: "5px",
              marginTop: "-20px", marginBottom: "20px"
            }}>
              {formik?.errors["allSearchFieldsEmpty"]}
            </div>
          )}
        </form>
        <div className="table-wrap mt-3">
          <Table responsive>
            <thead>
              <tr>
                <th></th>
                {patientListTitle?.map((title, i) => (
                  <th key={i}>{title}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderRows()}
            </tbody>
          </Table>
          {filterData?.length > 0 ? (
            <div>
              <CustomPagination
                tableData={filterData}
                totalRows={filterData?.length}
                rowsPerPage={rowsPerPage}
                page={page}
                handlePage={(pageNo) => setPage(pageNo)}
              />
            </div>
          ) : null}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!selectedPatientId}
          onClick={() => {
            if (selectedPatientObj) {
              onPatientSelected(selectedPatientObj);
            }
          }}
        >
          Select
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
